import React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { useControllableState } from "replo-utils/react/use-controllable-state";

type ChipProps = React.PropsWithChildren<{
  size: "base" | "sm" | "lg";
  UNSAFE_className?: string;
  layoutClassName?: string;
  startEnhancer?: React.ReactNode;
  active?: boolean;
  onChange?: () => void;
  tooltipText?: string;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  variant?: "default" | "empty";
  showHoverStateWhenActive?: boolean;
  UNSAFE_activeClassName?: string;
}>;

const SelectableChip: React.FC<
  ChipProps & Omit<React.HTMLAttributes<HTMLButtonElement>, "className">
> = ({
  size,
  children,
  UNSAFE_className,
  layoutClassName,
  startEnhancer,
  active,
  onChange,
  onClick,
  tooltipText,
  disabled,
  variant = "default",
  showHoverStateWhenActive = true,
  UNSAFE_activeClassName,
  ...domProps
}) => {
  const [isActive, setIsActive] = useControllableState<boolean>(
    active,
    false,
    onChange,
  );

  const onChipClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (onClick) {
      onClick(e);
    }
    setIsActive(!isActive);
  };

  const chipSize = {
    sm: "text-xs py-[6px] px-2 h-6",
    base: "text-sm py-2 px-3 h-[32px]",
    lg: "text-base py-2 px-4",
  };

  const chipComponent = (
    <button
      type="button"
      className={twMerge(
        "border-0.5 border-border text-muted hover:bg-selectable-hover flex cursor-pointer items-center justify-center gap-[6px] rounded-md bg-white font-normal",
        chipSize[size],
        isActive && "border-primary bg-selectable-selected text-primary",
        showHoverStateWhenActive &&
          isActive &&
          "hover:bg-selectable-selected-hover",
        !showHoverStateWhenActive && isActive && "hover:bg-selectable-selected",
        UNSAFE_activeClassName && isActive && UNSAFE_activeClassName,
        disabled && "cursor-not-allowed opacity-50",
        variant === "empty" &&
          "bg-sheet hover:bg-menu-item-hover/45 border-dashed",
        layoutClassName,
        UNSAFE_className,
      )}
      onClick={onChipClick}
      disabled={disabled}
      {...domProps}
    >
      {startEnhancer}
      <div className="flex-1 truncate">{children}</div>
    </button>
  );
  return tooltipText ? (
    <Tooltip content={tooltipText} triggerAsChild>
      {chipComponent}
    </Tooltip>
  ) : (
    chipComponent
  );
};

export default SelectableChip;
